import { createSlice } from "@reduxjs/toolkit";
import { initialState } from "../initialState.js";
import {
  completeRepair,
  createCommercialOffer,
  createDiagnostic,
  getClientsInWork,
  getCommercialOffer,
  getCommercialOfferData,
  getDataForOrder,
  getDiagnostic,
  getNodesAndParts,
  getReceivingDetails,
  getPartsSearchStatus,
  getRepair,
  getStatsList,
  updateRepair,
  changeRepairCompletedDate,
  updateRepairParts,
  updateCommercialOffer,
  getDiagnosticTree,
  getPartDetails,
  getPartsOfferByOEM,
  sendComOffer,
  getBufferDiagnostic,
  getParticularOrderByID,
  sendOrder,
  startAdditionalSearch,
  getAdditionalOffer,
  manuallyApproveComOffer,
  updateRepairService,
  deletePartFromComOffer,
  deleteServiceFromComOffer,
  getRecordInfo,
  restartSearchParts,
  deleteReceivingDocument,
  deleteRepair,
  deleteOrder,
  deleteCommercialOffer,
  deleteDiagnostic,
  patchDiagnostic,
  returnCarToCustomer,
} from "./operations.js";

const handlePending = state => {
  state.isLoading = true;
  state.error = null;
};

const handleRejected = (state, action) => {
  state.isLoading = false;
  state.error = action.payload;
};

const AccountingSlice = createSlice({
  name: "accounting",
  initialState: initialState.accounting,
  reducers: {
    clearDiag: state => {
      state.diagnostic = {};
      state.error = null;
      state.isDiagCreateLoading = false;
    },
    clearCommercialOfferData: state => {
      state.commercialOfferData = {};
    },
    clearRepair: state => {
      state.repairDetails = {};
      state.isRepairLoading = false;
      state.error = null;
    },
    clearCarsList: state => {
      state.clientsList = [];
      state.carsListError = null;
    },
    setChosenPeriod: (state, action) => {
      state.chosenPeriod = action.payload;
    },
    clearChosenPeriod: state => {
      state.chosenPeriod = {};
    },
    clearExistedComOffer: state => {
      state.commercialOffer = {};
    },
    clearDiagTree: state => {
      state.diagnosticTree = {};
    },
    clearDiagPartDetails: state => {
      state.partDetailsForDiagnostic = {};
    },
    clearBufferDiagnostic: state => {
      state.bufferDiagnostic = {};
    },
    clearDiagnosticId: state => {
      state.diagnosticId = null;
    },
    clearPartOffersByOEM: state => {
      state.partOffersByOEM = [];
    },
    clearReceivingDetails: state => {
      state.receiving = {};
    },
    clearAdditionalSearchParts: state => {
      state.additionalSearchParts = {};
    },
    setChosenPartsInDiag: (state, action) => {
      state.chosenPartsInDiag = action.payload;
    },
    setModalCarId: (state, action) => {
      state.modalCarId = action.payload;
    },
    setGlobalOfferObj: (state, action) => {
      state.globalOfferObj = action.payload;
    },
    setNewPartInRepair: (state, action) => {
      state.newPartInRepair = action.payload;
    },
    setIsAddingNewRow: (state, action) => {
      state.isAddingNewRow = action.payload;
    },
  },
  extraReducers: builder =>
    builder
      // ! Statistics
      // Get stats list +
      .addCase(getStatsList.pending, state => {
        state.isStatsLoading = true;
        state.error = null;
      })
      .addCase(getStatsList.fulfilled, (state, action) => {
        state.isStatsLoading = false;
        state.stats = action.payload;
      })
      .addCase(getStatsList.rejected, (state, action) => {
        state.isStatsLoading = false;
        state.error = action.payload;
      })

      // ! Clients in work list
      // Get list of all clients in work +
      .addCase(getClientsInWork.pending, (state, action) => {
        state.isCarsListLoading = true;
        state.carsListError = null;
      })
      .addCase(getClientsInWork.fulfilled, (state, action) => {
        state.isCarsListLoading = false;
        state.clientsList = action.payload.records;
      })
      .addCase(getClientsInWork.rejected, (state, action) => {
        state.isCarsListLoading = false;
        state.carsListError = action.payload;
      })

      // Get parts search status
      .addCase(getPartsSearchStatus.pending, (state, action) => {
        state.error = null;
      })
      .addCase(getPartsSearchStatus.fulfilled, (state, action) => {
        const diagnostic_id = action.meta.arg;

        // Записуємо статус для відповідного diagnostic_id
        state.partsSearchStatuses[diagnostic_id] = action.payload;
      })
      .addCase(getPartsSearchStatus.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Get parts search status +
      .addCase(getRecordInfo.pending, (state, action) => {
        state.isRecordInfoLoading = true;
        state.error = null;
      })
      .addCase(getRecordInfo.fulfilled, (state, action) => {
        state.isRecordInfoLoading = false;
        state.recordInfo = action.payload;
      })
      .addCase(getRecordInfo.rejected, (state, action) => {
        state.isRecordInfoLoading = false;
        state.error = action.payload;
      })

      //! DIAGNOSTICS
      // Get Buffer Diagnostic +
      .addCase(getBufferDiagnostic.pending, (state, action) => {
        state.isBufferDiagnosticLoading = true;
        state.error = null;
      })
      .addCase(getBufferDiagnostic.fulfilled, (state, action) => {
        state.isBufferDiagnosticLoading = false;
        state.bufferDiagnostic = action.payload;
      })
      .addCase(getBufferDiagnostic.rejected, (state, action) => {
        state.isBufferDiagnosticLoading = false;
        state.error = action.payload;
      })

      // Get tree of categories and parts +
      .addCase(getDiagnosticTree.pending, (state, action) => {
        state.isDiagnosticTreeLoading = true;
        state.error = null;
      })
      .addCase(getDiagnosticTree.fulfilled, (state, action) => {
        state.isDiagnosticTreeLoading = false;
        state.diagnosticTree = action.payload;
      })
      .addCase(getDiagnosticTree.rejected, (state, action) => {
        state.isDiagnosticTreeLoading = false;
        state.error = action.payload;
      })

      // Get part details from diagnostic tree +
      .addCase(getPartDetails.pending, (state, action) => {
        state.isPartDetailsForDiagnosticLoading = true;
        state.error = null;
      })
      .addCase(getPartDetails.fulfilled, (state, action) => {
        state.isPartDetailsForDiagnosticLoading = false;
        state.partDetailsForDiagnostic = action.payload;
      })
      .addCase(getPartDetails.rejected, (state, action) => {
        state.isPartDetailsForDiagnosticLoading = false;
        state.error = action.payload;
      })

      // Get Nodes and Parts list +
      .addCase(getNodesAndParts.pending, handlePending)
      .addCase(getNodesAndParts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nodesAndPartsForDiagnostics = action.payload;
      })
      .addCase(getNodesAndParts.rejected, handleRejected)

      .addCase(getDiagnostic.pending, (state, action) => {
        state.isDiagLoading = true;
        state.error = null;
      })
      .addCase(getDiagnostic.fulfilled, (state, action) => {
        state.isDiagLoading = false;
        state.diagnostic = action.payload;
      })
      .addCase(getDiagnostic.rejected, (state, action) => {
        state.isDiagLoading = false;
        state.error = action.payload;
      })

      // Create diagnostic +
      .addCase(createDiagnostic.pending, (state, action) => {
        state.isDiagCreateLoading = true;
        state.error = null;
      })
      .addCase(createDiagnostic.fulfilled, (state, action) => {
        state.isDiagCreateLoading = false;
        state.diagnosticId = action.payload.id;
      })
      .addCase(createDiagnostic.rejected, handleRejected)

      // Patch diagnostic +
      .addCase(patchDiagnostic.pending, (state, action) => {
        state.isDiagLoading = true;
        state.error = null;
      })
      .addCase(patchDiagnostic.fulfilled, (state, action) => {
        state.isDiagLoading = false;
        state.diagnostic = action.payload;
      })
      .addCase(patchDiagnostic.rejected, (state, action) => {
        state.isDiagLoading = false;
        state.error = null;
      })

      // Restart search parts +
      .addCase(restartSearchParts.pending, (state, action) => {
        state.isDiagCreateLoading = true;
        state.error = null;
      })
      .addCase(restartSearchParts.fulfilled, (state, action) => {
        state.isDiagCreateLoading = false;
        state.diagnosticId = action.payload.diagnostic_id;
      })
      .addCase(restartSearchParts.rejected, handleRejected)

      // Delete diagnostic +
      .addCase(deleteDiagnostic.pending, (state, action) => {
        state.isDiagCreateLoading = true;
        state.error = null;
      })
      .addCase(deleteDiagnostic.fulfilled, (state, action) => {
        state.isDiagCreateLoading = false;
        state.diagnosticId = null;
        state.diagnostic = {};
      })
      .addCase(deleteDiagnostic.rejected, handleRejected)

      //! COMMERCIAL OFFER
      .addCase(getCommercialOfferData.pending, (state, action) => {
        state.isCOLoading = true;
        state.error = null;
      })
      .addCase(getCommercialOfferData.fulfilled, (state, action) => {
        state.isCOLoading = false;
        state.commercialOfferData = action.payload;
      })
      .addCase(getCommercialOfferData.rejected, (state, action) => {
        state.isCOLoading = false;
        state.error = action.payload;
      })

      // Create Commercial Offer +
      .addCase(createCommercialOffer.pending, handlePending)
      .addCase(createCommercialOffer.fulfilled, state => {
        state.isLoading = false;
      })
      .addCase(createCommercialOffer.rejected, handleRejected)

      // Get Commercial Offer +
      .addCase(getCommercialOffer.pending, (state, action) => {
        state.isCOLoading = true;
        state.error = null;
      })
      .addCase(getCommercialOffer.fulfilled, (state, action) => {
        state.isCOLoading = false;
        state.commercialOffer = action.payload;
      })
      .addCase(getCommercialOffer.rejected, (state, action) => {
        state.isCOLoading = false;
        state.error = action.payload;
      })
      .addCase(updateCommercialOffer.pending, handlePending)
      .addCase(updateCommercialOffer.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updateCommercialOffer.rejected, handleRejected)

      // Get parts offer by OEM +
      .addCase(getPartsOfferByOEM.pending, (state, action) => {
        state.isPartOffersByOEMLoading = true;
        state.error = null;
      })
      .addCase(getPartsOfferByOEM.fulfilled, (state, action) => {
        state.isPartOffersByOEMLoading = false;
        state.partOffersByOEM = action?.payload?.part?.suppliers;
      })
      .addCase(getPartsOfferByOEM.rejected, (state, action) => {
        state.isPartOffersByOEMLoading = false;
        state.error = action.payload;
      })

      // Send commercial offer to client for approval +
      .addCase(sendComOffer.pending, (state, action) => {
        state.error = null;
      })
      .addCase(sendComOffer.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Manually Approve commercial offer +
      .addCase(manuallyApproveComOffer.pending, (state, action) => {
        state.error = null;
      })
      .addCase(manuallyApproveComOffer.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(startAdditionalSearch.pending, (state, action) => {
        state.error = null;
      })
      .addCase(startAdditionalSearch.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(getAdditionalOffer.pending, (state, action) => {
        state.error = null;
      })
      .addCase(getAdditionalOffer.fulfilled, (state, action) => {
        state.additionalSearchParts = action.payload;
      })
      .addCase(getAdditionalOffer.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete part from commercial offer +
      .addCase(deletePartFromComOffer.pending, state => {
        // state.isLoading = true;
      })
      .addCase(deletePartFromComOffer.fulfilled, (state, action) => {
        state.isLoading = false;
        console.log(
          "action.meta.arg in deletePartFromComOffer.fulfilled: ",
          action.meta.arg
        );
        const deletedPartId = action.meta.arg.part_id;

        // Видаляємо видалену деталь з масиву деталей в state.commercialOffer
        state.commercialOffer.all_parts =
          state.commercialOffer.all_parts.filter(
            part => part.id !== deletedPartId
          );
      })
      .addCase(deletePartFromComOffer.rejected, (state, action) => {
        // state.isLoading = false;
        state.error = action.payload;
      })

      // Delete service from commercial offer +
      .addCase(deleteServiceFromComOffer.pending, state => {
        // state.isLoading = true;
      })
      .addCase(deleteServiceFromComOffer.fulfilled, (state, action) => {
        state.isLoading = false;
        // console.log(
        //   "action.meta.arg in deleteServiceFromComOffer.fulfilled: ",
        //   action.meta.arg
        // );
        // const deletedServiceId = action.meta.arg.service_id;

        // // Видаляємо видалену послугу з масиву послуг в state.commercialOffer
        // state.commercialOffer.services_list =
        //   state.commercialOffer.services_list.filter(
        //     service => service.id !== deletedServiceId
        //   );
      })
      .addCase(deleteServiceFromComOffer.rejected, (state, action) => {
        // state.isLoading = false;
        state.error = action.payload;
      })

      // Delete commercial offer +
      .addCase(deleteCommercialOffer.pending, state => {
        state.isLoading = true;
      })
      .addCase(deleteCommercialOffer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.commercialOffer = {};
      })
      .addCase(deleteCommercialOffer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      //! Order
      .addCase(getDataForOrder.pending, handlePending)
      .addCase(getDataForOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.order = action.payload;
      })
      .addCase(getDataForOrder.rejected, handleRejected)

      // Send Order +
      .addCase(sendOrder.pending, handlePending)
      .addCase(sendOrder.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(sendOrder.rejected, handleRejected)

      // Get particular order by ID +
      .addCase(getParticularOrderByID.pending, state => {
        state.isParticularOrderBySupplierLoading = true;
        state.error = null;
      })
      .addCase(getParticularOrderByID.fulfilled, (state, action) => {
        state.isParticularOrderBySupplierLoading = false;
        state.particularOrderBySupplier = action.payload;
      })
      .addCase(getParticularOrderByID.rejected, (state, action) => {
        state.isParticularOrderBySupplierLoading = false;
        state.error = action.payload;
      })

      // Delete order +
      .addCase(deleteOrder.pending, handlePending)
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.particularOrderBySupplier = {};
      })
      .addCase(deleteOrder.rejected, handleRejected)

      // ! RECEIVED PARTS

      // Get details of the receiving of order from particular supplier +
      .addCase(getReceivingDetails.pending, handlePending)
      .addCase(getReceivingDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.receiving = action.payload;
      })
      .addCase(getReceivingDetails.rejected, handleRejected)

      // Delete receiving document +
      .addCase(deleteReceivingDocument.pending, handlePending)
      .addCase(deleteReceivingDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.receiving = {};
      })
      .addCase(deleteReceivingDocument.rejected, handleRejected)

      // ! Repair
      // Get repair info +
      .addCase(getRepair.pending, (state, action) => {
        state.isRepairLoading = true;
        state.error = null;
      })
      .addCase(getRepair.fulfilled, (state, action) => {
        state.isRepairLoading = false;
        state.repairDetails = action.payload;
      })
      .addCase(getRepair.rejected, (state, action) => {
        state.isRepairLoading = false;
        state.error = action.payload;
      })

      .addCase(updateRepair.pending, handlePending)
      .addCase(updateRepair.fulfilled, (state, action) => {
        state.isLoading = false;
        const { type_payment } = action.meta.arg;
        if (type_payment && state.repairDetails) {
          state.repairDetails.type_payment = type_payment;
        }
      })
      .addCase(updateRepair.rejected, handleRejected)

      // Change repair completed date
      .addCase(changeRepairCompletedDate.pending, handlePending)
      .addCase(changeRepairCompletedDate.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.repairDetails) {
          state.repairDetails.finished_at = action.meta.arg.complete_date;
        }
      })
      .addCase(changeRepairCompletedDate.rejected, handleRejected)

      // Update repair part status +
      .addCase(updateRepairParts.pending, handlePending)
      .addCase(updateRepairParts.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedPart = action.payload;
        const partIndex = state.repairDetails.parts.findIndex(
          part => part.id === updatedPart.id
        );
        if (partIndex !== -1) {
          state.repairDetails.parts[partIndex] = {
            ...state.repairDetails.parts[partIndex],
            ...updatedPart,
          };
        }
      })
      .addCase(updateRepairParts.rejected, handleRejected)

      // Update repair service +
      .addCase(updateRepairService.pending, handlePending)
      .addCase(updateRepairService.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedService = action.payload;
        const serviceIndex = state.repairDetails.services.findIndex(
          service => service.id === updatedService.id
        );
        if (serviceIndex !== -1) {
          state.repairDetails.services[serviceIndex] = {
            ...state.repairDetails.services[serviceIndex],
            ...updatedService,
          };
        }
      })
      .addCase(updateRepairService.rejected, handleRejected)

      .addCase(completeRepair.pending, handlePending)
      .addCase(completeRepair.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(completeRepair.rejected, handleRejected)

      // Delete repair +
      .addCase(deleteRepair.pending, handlePending)
      .addCase(deleteRepair.fulfilled, (state, action) => {
        state.isLoading = false;
        state.repairDetails = {};
      })
      .addCase(deleteRepair.rejected, handleRejected)
  
  // ! RETURN CAR TO THE CUSTOMER
      .addCase(returnCarToCustomer.pending, handlePending)
      .addCase(returnCarToCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(returnCarToCustomer.rejected, handleRejected)
});

export const {
  clearDiag,
  clearCommercialOfferData,
  clearRepair,
  clearCarsList,
  setChosenPeriod,
  clearChosenPeriod,
  clearExistedComOffer,
  clearDiagTree,
  clearDiagPartDetails,
  clearBufferDiagnostic,
  clearDiagnosticId,
  clearPartOffersByOEM,
  clearReceivingDetails,
  clearAdditionalSearchParts,
  setChosenPartsInDiag,
  setModalCarId,
  setGlobalOfferObj,
  setNewPartInRepair,
  setIsAddingNewRow,
} = AccountingSlice.actions;
export default AccountingSlice.reducer;
